import {
  StandardTableCell,
  StandardTableRow
} from '@components/UI-Components/StandardTableUnits/TableUnits'
import { AppliedFilterIcon, FilterIcon } from '@components/UI-Components/svg'
import { FontTShirtSize, StickyStyle, TextAlignmentFlexMap } from '@modules/common/models'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { strings } from '@stores'
import classNames from 'classnames'
import { Observer, useObserver } from 'mobx-react-lite'
import React, { Fragment } from 'react'
import { VariableSizeList as List } from 'react-window'
import styled from 'styled-components'
import {
  HEADERROWBORDERWIDTH,
  MULTI_SELECT_COLUMN_HEADER_WIDTH,
  MULTI_SELECT_COLUMN_WIDTH
} from '../common/constants'
import { TableViewStoreContext } from '../container/StandardTableView'
import * as css from './TableView.css'
import map from 'lodash/map'
import { TableViewColumnDefinition } from '../models/TableViewColumnDefinition'
import { useTranslation } from 'react-i18next'

interface ITableHeaderProps {
  visibleHeight: number
  visibleWidth: number
  rowHeight: number
  rowWidth: number
  stickyCols: Array<StickyStyle>
  colDef: Array<TableViewColumnDefinition>
  allowMultiSelect?: boolean
  hideHeader?: boolean
  maxSelectionLimit?: number
  canSelectAll?: boolean
  hideHeaderTopBorder?: boolean
  isTableDisabled?: boolean
}

interface HeaderContentProps {
  alignment?: string
}

const StyledDiv = styled.div<HeaderContentProps>(props => {
  const { alignment } = props
  return {
    '&&': {
      font: FontTShirtSize.xs,
      padding: '9px 0px 9px 0px',
      whiteSpace: 'nowrap',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: alignment
        ? TextAlignmentFlexMap[alignment] || TextAlignmentFlexMap.left
        : TextAlignmentFlexMap.left
    }
  }
})

export const TableHeader: React.FunctionComponent<ITableHeaderProps> = ({
  visibleHeight,
  visibleWidth,
  rowWidth,
  rowHeight,
  colDef,
  stickyCols,
  allowMultiSelect,
  hideHeader,
  maxSelectionLimit,
  canSelectAll,
  hideHeaderTopBorder,
  isTableDisabled
}) => {
  return useObserver(function useHook() {
    const {
      setTableHeaderRef,
      openFilterPopOver,
      filteredColumns,
      dataForBulkUpdateComponent,
      onAllSelectCheckboxClick,
      isSelectAllSet,
      canCheckedSelectAll
    } = React.useContext(TableViewStoreContext) as any
    const { t } = useTranslation()

    const HeaderContainerRef: any = containerRef => {
      if (containerRef) {
        setTableHeaderRef(containerRef)
      }
    }

    const selectAllLimitMsg = strings.selectAllLimitMsg.replace(
      '$$MAX_SELECTION_LIMIT$$',
      `${maxSelectionLimit}`
    )

    const headerRenderer = ({ rowIndex, style }) => {
      return (
        <StandardTableRow
          style={style}
          rowBottomBorderWidth={hideHeaderTopBorder ? 1 : HEADERROWBORDERWIDTH}
          rowTopBorderWidth={hideHeaderTopBorder ? 0 : HEADERROWBORDERWIDTH}
          borderColor={hideHeaderTopBorder ? '#979797' : 'var(--background-grey-5)'}
          width={rowWidth}
        >
          <Observer>
            {() => (
              <>
                {allowMultiSelect && (
                  <StandardTableCell
                    // If header is not present then show select all checkbox
                    cellWidth={
                      hideHeader
                        ? MULTI_SELECT_COLUMN_HEADER_WIDTH
                        : MULTI_SELECT_COLUMN_WIDTH
                    }
                    cellPad={'0px 9px 0px 9px'}
                    totalBorderHeight={HEADERROWBORDERWIDTH}
                    isUpperCase={true}
                    stickyLeft={0}
                  >
                    {canCheckedSelectAll.canSelectAll ? (
                      <FormControlLabel
                        id={'value'}
                        className={css.formLable}
                        classes={{ root: css.formControlLabel }}
                        control={
                          <Tooltip
                            title={
                              !canCheckedSelectAll.canSelectAll &&
                              !isSelectAllSet &&
                              canCheckedSelectAll.showMaxLimitMessage
                                ? selectAllLimitMsg
                                : ''
                            }
                          >
                            <Checkbox
                              onChange={
                                isSelectAllSet ||
                                (!isSelectAllSet && canCheckedSelectAll.canSelectAll)
                                  ? onAllSelectCheckboxClick
                                  : null
                              }
                              classes={{ root: css.checkbox }}
                              checked={isSelectAllSet}
                              value={''}
                              color='default'
                              disabled={
                                canCheckedSelectAll.disableSelectAll || isTableDisabled
                              }
                            />
                          </Tooltip>
                        }
                        label={
                          <Typography className={hideHeader ? css.headerCheckBox : null}>
                            {hideHeader ? 'All' : ''}
                          </Typography>
                        }
                      />
                    ) : null}
                  </StandardTableCell>
                )}
                {!hideHeader &&
                  map(colDef, (col: TableViewColumnDefinition, colIndex) => {
                    return col?.isVisible ? (
                      <Fragment key={col.id}>
                        <StandardTableCell
                          cellWidth={col.width}
                          cellPad={col.isRootColumn ? '0px' : '0px 9px 0px 9px'}
                          stickyLeft={stickyCols[colIndex] && stickyCols[colIndex].left}
                          isCellShadow={
                            stickyCols[colIndex] &&
                            stickyCols[colIndex].shouldApplyCellShadow
                          }
                          totalBorderHeight={HEADERROWBORDERWIDTH}
                          isUpperCase={true}
                        >
                          <StyledDiv
                            className={classNames(css.tableHeader)}
                            alignment={col.props && (col.props.alignment as string)}
                          >
                            <div
                              className={classNames(
                                col.showColumnFilter ? css.cursor : null,
                                css.tableHeadertext
                              )}
                              onClick={
                                col.showColumnFilter
                                  ? openFilterPopOver(
                                      col.node.key,
                                      col.header.label,
                                      col.node.labelKey
                                    )
                                  : null
                              }
                            >
                              <div>
                                {col.showColumnFilter ? (
                                  <img
                                    src={
                                      filteredColumns[col.node.key]
                                        ? AppliedFilterIcon
                                        : FilterIcon
                                    }
                                    className={css.filterIcon}
                                    alt=''
                                  />
                                ) : null}
                              </div>
                              <Tooltip
                                title={
                                  col.header.label
                                    ? t(
                                        `bb.common.label.colHeaders.${col.id}`,
                                        col.header.label
                                      )
                                    : ''
                                }
                              >
                                <span
                                  className={classNames(
                                    css.headerText,
                                    col.header.className
                                  )}
                                >
                                  {col.header.label
                                    ? t(
                                        `bb.common.label.colHeaders.${col.id}`,
                                        col.header.label
                                      )
                                    : ''}
                                </span>
                              </Tooltip>
                            </div>
                            {col.header.action
                              ? col.header.action.component(
                                  dataForBulkUpdateComponent,
                                  col
                                )
                              : null}
                          </StyledDiv>
                        </StandardTableCell>
                      </Fragment>
                    ) : null
                  })}
              </>
            )}
          </Observer>
        </StandardTableRow>
      )
    }

    return canSelectAll || !hideHeader ? (
      <List
        className={css.HeaderGrid}
        outerRef={HeaderContainerRef}
        width={visibleWidth}
        height={visibleHeight}
        itemSize={() => rowHeight}
        itemCount={1}
      >
        {headerRenderer}
      </List>
    ) : null
  })
}
